import { router, useForm } from "@inertiajs/react";
import { useState } from "react";
import {
  Bell, BookOpen, CheckCircle, ChevronDown, ChevronUp, Plus, Sparkles, Users, X
} from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

interface SkmContact {
  id: number;
  firm_name: string;
  contact_name: string;
  contact_title: string;
  contact_email: string;
  contact_phone: string;
  status: string;
  notes: string;
  research_notes: string;
  research_generated_at: string | null;
  last_contacted_at: string | null;
  follow_up_at: string | null;
  updated_at: string;
}

interface Props {
  contacts: SkmContact[];
  due_followup: number;
  ai_assist: boolean;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  prospecting:       { label: "Prospecting",       className: "bg-gray-100 text-gray-600" },
  contacted:         { label: "Contacted",          className: "bg-blue-100 text-blue-700" },
  replied:           { label: "Replied",            className: "bg-green-100 text-green-700" },
  meeting_scheduled: { label: "Meeting Scheduled",  className: "bg-purple-100 text-purple-700" },
  placed:            { label: "Placed",             className: "bg-amber-100 text-amber-700" },
  inactive:          { label: "Inactive",           className: "bg-gray-100 text-gray-400" },
};

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, { label }]) => ({ value, label }));

function NewContactForm({ onClose }: { onClose: () => void }) {
  const form = useForm({
    firm_name:     "",
    contact_name:  "",
    contact_title: "",
    contact_email: "",
    contact_phone: "",
    status:        "prospecting",
    notes:         "",
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.post("/skm", { onSuccess: onClose });
  }

  return (
    <Card className="mb-6 border-green-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Add Contact</CardTitle>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="firm_name">Firm</Label>
              <Input id="firm_name" value={form.data.firm_name} onChange={(e) => form.setData("firm_name", e.target.value)} className="mt-1" placeholder="Sullivan & Cromwell" required />
            </div>
            <div>
              <Label htmlFor="contact_name">Contact Name</Label>
              <Input id="contact_name" value={form.data.contact_name} onChange={(e) => form.setData("contact_name", e.target.value)} className="mt-1" placeholder="Jane Smith" required />
            </div>
            <div>
              <Label htmlFor="contact_title">Title</Label>
              <Input id="contact_title" value={form.data.contact_title} onChange={(e) => form.setData("contact_title", e.target.value)} className="mt-1" placeholder="Partner, M&A" />
            </div>
            <div>
              <Label htmlFor="contact_email">Email</Label>
              <Input id="contact_email" type="email" value={form.data.contact_email} onChange={(e) => form.setData("contact_email", e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" value={form.data.notes} onChange={(e) => form.setData("notes", e.target.value)} rows={2} className="mt-1" />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={form.processing} size="sm" className="bg-green-700 hover:bg-green-800 text-white">Save Contact</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function ContactRow({ contact, ai_assist }: { contact: SkmContact; ai_assist: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [researching, setResearching] = useState(false);
  const statusCfg = STATUS_CONFIG[contact.status] ?? STATUS_CONFIG.prospecting;
  const overdue = contact.follow_up_at && new Date(contact.follow_up_at) < new Date();

  const editForm = useForm({
    status:           contact.status,
    last_contacted_at: contact.last_contacted_at?.split("T")[0] ?? "",
    follow_up_at:     contact.follow_up_at?.split("T")[0] ?? "",
    notes:            contact.notes,
  });

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    editForm.patch(`/skm/${contact.id}`, { onSuccess: () => setExpanded(false) });
  }

  function handleResearch() {
    setResearching(true);
    router.post(`/skm/${contact.id}/research`, {}, { onFinish: () => setResearching(false) });
  }

  return (
    <div className="border-b border-gray-100 last:border-0">
      <div
        className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">{contact.contact_name}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${statusCfg.className}`}>
              {statusCfg.label}
            </span>
            {overdue && (
              <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                <Bell className="w-2.5 h-2.5" /> Follow up
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {[contact.firm_name, contact.contact_title].filter(Boolean).join(" · ")}
          </p>
        </div>
        <div className="flex items-center gap-3 ml-4 shrink-0">
          {contact.last_contacted_at && (
            <span className="text-xs text-gray-400">
              Last: {new Date(contact.last_contacted_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </span>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 bg-gray-50/50">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2">
            {/* Edit form */}
            <form onSubmit={handleSave} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Status</Label>
                  <select
                    value={editForm.data.status}
                    onChange={(e) => editForm.setData("status", e.target.value)}
                    className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-600"
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Follow-up Date</Label>
                  <Input type="date" value={editForm.data.follow_up_at} onChange={(e) => editForm.setData("follow_up_at", e.target.value)} className="mt-1 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Last Contacted</Label>
                  <Input type="date" value={editForm.data.last_contacted_at} onChange={(e) => editForm.setData("last_contacted_at", e.target.value)} className="mt-1 text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={editForm.data.notes} onChange={(e) => editForm.setData("notes", e.target.value)} rows={3} className="mt-1 text-sm" />
              </div>
              <Button type="submit" disabled={editForm.processing} size="sm" className="bg-green-700 hover:bg-green-800 text-white">
                Save
              </Button>
            </form>

            {/* Research notes */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs flex items-center gap-1">
                  <BookOpen className="w-3 h-3" /> Research Notes
                </Label>
                {ai_assist && (
                  <button
                    onClick={handleResearch}
                    disabled={researching}
                    className="text-xs text-purple-700 border border-purple-200 hover:bg-purple-50 rounded px-2 py-0.5 flex items-center gap-1 transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-3 h-3" />
                    {researching ? "Researching..." : contact.research_notes ? "Refresh" : "AI Research"}
                  </button>
                )}
              </div>
              {contact.research_notes ? (
                <div className="bg-white border border-purple-100 rounded-lg p-3">
                  <p className="text-xs text-gray-600 leading-relaxed">{contact.research_notes}</p>
                  {contact.research_generated_at && (
                    <p className="text-[10px] text-purple-400 mt-2 flex items-center gap-0.5">
                      <Sparkles className="w-2.5 h-2.5" />
                      Generated {new Date(contact.research_generated_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-white border border-gray-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-400">
                    {ai_assist ? "Click AI Research to generate notes." : "Enable AI Assist to generate research notes."}
                  </p>
                </div>
              )}

              {contact.contact_email && (
                <div className="mt-3 text-xs text-gray-500 space-y-1">
                  {contact.contact_email && <p><span className="font-medium">Email:</span> {contact.contact_email}</p>}
                  {contact.contact_phone && <p><span className="font-medium">Phone:</span> {contact.contact_phone}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SkmIndex({ contacts, due_followup, ai_assist }: Props) {
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = statusFilter === "all"
    ? contacts
    : contacts.filter((c) => c.status === statusFilter);

  return (
    <AppLayout title="SKM">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SKM</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Strategic Key Market contacts · {contacts.length} total
            {due_followup > 0 && (
              <span className="ml-2 text-red-600 font-medium">{due_followup} follow-ups due</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {ai_assist && (
            <span className="text-xs font-medium text-purple-700 bg-purple-50 border border-purple-100 rounded px-2 py-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> AI Assist on
            </span>
          )}
          <Button size="sm" onClick={() => setShowForm(true)} className="bg-green-700 hover:bg-green-800 text-white">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Contact
          </Button>
        </div>
      </div>

      {showForm && <NewContactForm onClose={() => setShowForm(false)} />}

      {/* Status filter */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {["all", ...Object.keys(STATUS_CONFIG)].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-3 py-1 rounded-full border transition-colors font-medium ${
              statusFilter === s
                ? "bg-gray-900 text-white border-gray-900"
                : "border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {s === "all" ? "All" : STATUS_CONFIG[s]?.label ?? s}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No contacts yet. Add your first SKM contact to get started.</p>
          </div>
        ) : (
          filtered.map((c) => <ContactRow key={c.id} contact={c} ai_assist={ai_assist} />)
        )}
      </div>
    </AppLayout>
  );
}
